import { get, post } from '../../../Services/http';

const BASE_URL = '/api/bft-management';
const POLL_INTERVAL_MS = 1000;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

const readJson = async (res) => {
    try {
        return await res.json();
    } catch {
        return null;
    }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The http service throws for non-2xx responses and puts the response on the error
const toError = async (err) => {
    if (!err.response) return err;

    const data = await readJson(err.response);
    return new Error(data?.error || `HTTP ${err.response.status}`);
};

// Calls a short management endpoint (GET when no body is given) and resolves with the json response.
// An AbortController can be given for a POST to cancel the request.
export const callApi = async (endpoint, body, abortController = null) => {
    try {
        const res = await (body === undefined ? get(`${BASE_URL}/${endpoint}`) : post(`${BASE_URL}/${endpoint}`, body, abortController));
        return await readJson(res);
    } catch (err) {
        throw await toError(err);
    }
};

// Starts a background job on the server. Resolves with the job (also for 409, where the job is already running).
export const startJob = async (endpoint, body) => {
    try {
        const res = await post(`${BASE_URL}/${endpoint}`, body ?? {});
        const data = await readJson(res);
        return data?.job ?? data;
    } catch (err) {
        if (err.response?.status === 409) {
            const data = await readJson(err.response);
            if (data?.job) return data.job;
        }
        throw await toError(err);
    }
};

// Polls the job until it has finished. Because the work runs in the background on the server there is no request timeout.
export const waitForJob = async (jobId, { onUpdate, isCancelled } = {}) => {
    let failures = 0;

    while (!isCancelled?.()) {
        await sleep(POLL_INTERVAL_MS);
        if (isCancelled?.()) break;

        try {
            const res = await get(`${BASE_URL}/jobs/${encodeURIComponent(jobId)}`);

            failures = 0;
            const job = await res.json();
            onUpdate?.(job);
            if (job.state !== 'running') return job;
        } catch (err) {
            if (err.response?.status === 404) {
                throw new Error('Jobbet hittades inte (servern kan ha startats om).');
            }

            failures += 1;
            if (failures >= MAX_CONSECUTIVE_POLL_FAILURES) throw err.response ? new Error(`HTTP ${err.response.status}`) : err;
        }
    }

    return null;
};
